/**
 * Tenant Vocabulary / Nomenclature.
 *
 * Allows different business verticals (Restoration, Commercial GC, Field Service, Van Shops)
 * to customize terminology across the interface while keeping the exact same high-efficiency
 * underlying engine.
 */

import { tenantGetJSON, tenantSetJSON } from '@/product/lib/tenantStorage';

export type VocabularyPreset = 'restoration' | 'construction' | 'service' | 'consulting' | 'custom';

export interface TenantVocabulary {
    preset: VocabularyPreset;
    /** Single unit of work: Job, Project, Work Order, Claim, File */
    jobSingular: string;
    jobPlural: string;
    /** Customer entity: Customer, Client, Policyholder, Account */
    customerSingular: string;
    customerPlural: string;
    /** Front office / lead capture: Lead, Inquiry, Claim Intake, Request */
    leadSingular: string;
    leadPlural: string;
    /** Field team: Crew, Team, Technician, Subcontractor */
    crewSingular: string;
    crewPlural: string;
    /** Financial quote: Estimate, Proposal, Bid, Quote */
    estimateSingular: string;
    estimatePlural: string;
    /** Work proof: Field Evidence, Daily Logs, Inspection Proof, Site Reports */
    evidenceLabel: string;
}

export const VOCABULARY_STORAGE_KEY = 'siteline_tenant_vocabulary_v1';

export const VOCABULARY_PRESETS: Record<VocabularyPreset, TenantVocabulary> = {
    restoration: {
        preset: 'restoration',
        jobSingular: 'Job',
        jobPlural: 'Jobs',
        customerSingular: 'Customer',
        customerPlural: 'Customers',
        leadSingular: 'Lead',
        leadPlural: 'Leads',
        crewSingular: 'Crew',
        crewPlural: 'Crews',
        estimateSingular: 'Estimate',
        estimatePlural: 'Estimates',
        evidenceLabel: 'Field Evidence',
    },
    construction: {
        preset: 'construction',
        jobSingular: 'Project',
        jobPlural: 'Projects',
        customerSingular: 'Client',
        customerPlural: 'Clients',
        leadSingular: 'Bid Inquiry',
        leadPlural: 'Bid Inquiries',
        crewSingular: 'Sub / Crew',
        crewPlural: 'Subs & Crews',
        estimateSingular: 'Proposal',
        estimatePlural: 'Proposals',
        evidenceLabel: 'Site Reports',
    },
    service: {
        preset: 'service',
        jobSingular: 'Work Order',
        jobPlural: 'Work Orders',
        customerSingular: 'Customer',
        customerPlural: 'Customers',
        leadSingular: 'Service Call',
        leadPlural: 'Service Calls',
        crewSingular: 'Technician',
        crewPlural: 'Technicians',
        estimateSingular: 'Quote',
        estimatePlural: 'Quotes',
        evidenceLabel: 'Job Proof',
    },
    consulting: {
        preset: 'consulting',
        jobSingular: 'Engagement',
        jobPlural: 'Engagements',
        customerSingular: 'Client',
        customerPlural: 'Clients',
        leadSingular: 'Inquiry',
        leadPlural: 'Inquiries',
        crewSingular: 'Consultant',
        crewPlural: 'Consultants',
        estimateSingular: 'Proposal',
        estimatePlural: 'Proposals',
        evidenceLabel: 'Inspection Findings',
    },
    custom: {
        preset: 'custom',
        jobSingular: 'Job',
        jobPlural: 'Jobs',
        customerSingular: 'Customer',
        customerPlural: 'Customers',
        leadSingular: 'Lead',
        leadPlural: 'Leads',
        crewSingular: 'Crew',
        crewPlural: 'Crews',
        estimateSingular: 'Estimate',
        estimatePlural: 'Estimates',
        evidenceLabel: 'Field Evidence',
    },
};

export function getTenantVocabulary(): TenantVocabulary {
    if (typeof window === 'undefined') return { ...VOCABULARY_PRESETS.restoration };
    const stored = tenantGetJSON<Partial<TenantVocabulary> | null>(
        VOCABULARY_STORAGE_KEY,
        null
    );
    if (!stored) {
        return { ...VOCABULARY_PRESETS.restoration };
    }
    return { ...VOCABULARY_PRESETS.restoration, ...stored };
}

export function saveTenantVocabulary(
    vocab: Partial<TenantVocabulary>
): TenantVocabulary {
    const current = getTenantVocabulary();
    const updated = { ...current, ...vocab };
    tenantSetJSON(VOCABULARY_STORAGE_KEY, updated);
    return updated;
}

const WORD_SWAPS: { from: keyof TenantVocabulary; pattern: RegExp; lower: boolean }[] = [
    { from: 'jobPlural', pattern: /\bJobs\b/g, lower: false },
    { from: 'jobSingular', pattern: /\bJob\b/g, lower: false },
    { from: 'jobPlural', pattern: /\bjobs\b/g, lower: true },
    { from: 'jobSingular', pattern: /\bjob\b/g, lower: true },
    { from: 'customerPlural', pattern: /\bCustomers\b/g, lower: false },
    { from: 'customerSingular', pattern: /\bCustomer\b/g, lower: false },
    { from: 'customerPlural', pattern: /\bcustomers\b/g, lower: true },
    { from: 'customerSingular', pattern: /\bcustomer\b/g, lower: true },
    { from: 'crewPlural', pattern: /\bCrews\b/g, lower: false },
    { from: 'crewSingular', pattern: /\bCrew\b/g, lower: false },
    { from: 'crewPlural', pattern: /\bcrews\b/g, lower: true },
    { from: 'crewSingular', pattern: /\bcrew\b/g, lower: true },
    { from: 'estimatePlural', pattern: /\bEstimates\b/g, lower: false },
    { from: 'estimateSingular', pattern: /\bEstimate\b/g, lower: false },
    { from: 'estimatePlural', pattern: /\bestimates\b/g, lower: true },
    { from: 'estimateSingular', pattern: /\bestimate\b/g, lower: true },
    { from: 'leadPlural', pattern: /\bLeads\b/g, lower: false },
    { from: 'leadSingular', pattern: /\bLead\b/g, lower: false },
    { from: 'leadPlural', pattern: /\bleads\b/g, lower: true },
];

function cased(word: string, lower: boolean): string {
    return lower ? word.toLowerCase() : word;
}

/** Rewrite restoration nouns in a sentence when the tenant uses another preset. */
export function phraseWithVocabulary(text: string, vocab: TenantVocabulary): string {
    if (vocab.preset === 'restoration' && vocab.jobSingular === 'Job') return text;
    let next = text;
    for (const swap of WORD_SWAPS) {
        const replacement = cased(String(vocab[swap.from]), swap.lower);
        next = next.replace(swap.pattern, replacement);
    }
    return next;
}
